import React, {useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {Alert, Button, Form, Input, Typography, Result} from 'antd';
import {LockOutlined} from '@ant-design/icons';
import b2cAccountService from '../../services/b2cAccountService';
import BackToApp from './BackToApp';
import './AuthPages.css';

const {Title, Paragraph} = Typography;

/**
 * Cel linku „ustaw nowe hasło" z e-maila. O nowy link prosi się w aplikacji na
 * telefonie, bo strona nie ma logowania ani formularza odzyskiwania hasła.
 */
function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [form] = Form.useForm();

  const token = searchParams.get('token');

  const handleSubmit = async (values) => {
    if (!token) {
      setErrorMessage('Brak tokenu resetowania hasła.');
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      await b2cAccountService.resetPassword(token, values.newPassword);
      setSuccess(true);
    } catch (error) {
      const msg = error.response?.data?.message || 'Resetowanie hasła nie powiodło się.';
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <Result
            status="success"
            title="Hasło zmienione!"
            subTitle="Nowe hasło jest już aktywne. Wróć do aplikacji MyBeautyPlace na telefonie i zaloguj się."
            extra={<BackToApp />}
          />
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <Result
            status="error"
            title="Brak tokenu"
            subTitle="Link do resetowania hasła jest nieprawidłowy lub niepełny."
            extra={<BackToApp text="O nowy link poproś w aplikacji na telefonie, w ekranie logowania." />}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <Title level={2} className="auth-title">Ustaw nowe hasło</Title>
        <Paragraph className="auth-description">
          Wprowadź nowe hasło do swojego konta MyBeautyPlace.
        </Paragraph>

        <Form
          form={form}
          onFinish={handleSubmit}
          className="auth-form"
          layout="vertical"
        >
          <Form.Item
            name="newPassword"
            rules={[
              {required: true, message: 'Proszę podać nowe hasło!'},
              {min: 8, message: 'Hasło musi mieć co najmniej 8 znaków.'},
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Nowe hasło" size="large" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              {required: true, message: 'Proszę potwierdzić hasło!'},
              ({getFieldValue}) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Hasła nie są identyczne!'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Potwierdź hasło" size="large" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" className="auth-form-button" block loading={loading} size="large">
              Zmień hasło
            </Button>
          </Form.Item>
        </Form>

        {errorMessage && (
          <Alert
            message={errorMessage}
            type="error"
            showIcon
            style={{marginTop: 20}}
          />
        )}
      </div>
    </div>
  );
}

export default ResetPasswordPage;
